import CentricDispatcher from '../dispatcher/CentricDispatcher.react';
import { ActionTypes } from '../constants/CentricConstants';
import { ReduceStore } from 'flux/utils';
import update from 'immutability-helper';
import { toNumber } from '../utils/StringUtils';

const getEmptyState = (fetching) => ({isFetching: fetching || false, certificates: []});
const sanitise = ({id, fieldCount, paperSize, paperSizeId, watermarkAlpha, ...rest}) => ({
    id: Number(id),
    fieldCount: toNumber(fieldCount),
    paperSize: paperSize || null,
    paperSizeId: toNumber(paperSizeId),
    watermarkAlpha: toNumber(watermarkAlpha, 1),
    ...rest,
});

class CertificateStore extends ReduceStore {

    getIndex(id) {
        if (!id) { return -1; } // guard: id passed
        return this._state.certificates.findIndex(cert => cert.id === id);
    }

    getFieldIndex(id, fieldId) {
        let index = -1, fieldIndex = -1;
        if (!!id) {
            index = this.getIndex(id);
        }
        if (-1 < index && !!this._state.certificates[index].fields) {
            fieldIndex = this._state.certificates[index].fields.findIndex(field => field.id === fieldId);
        }
        return [index, fieldIndex];
    }
    
    getInitialState() {
        return getEmptyState();
    }

    isFetching() {
        return this.getState().isFetching;
    }

    getAll() {
        return this.getState().certificates;
    }

    getById(id) {
        return this.getState().certificates.find(cert => cert.id === id);
    }
    
    reduce(state, action) {
        let index, fieldIndex, loadDraft, certChange;
        switch (action.type) {
            case ActionTypes.CERTIFICATE_ADD_REQUEST:
                return update(this.getState(), { isFetching: { $set: true }});

            case ActionTypes.CERTIFICATE_ADD_SUCCESS:
                return update(this.getState(), {
                    isFetching: { $set: false },
                    certificates: { $push: [sanitise(action.data)] }
                });

            case ActionTypes.CERTIFICATE_ADD_FAILURE:
                return update(this.getState(), { isFetching: { $set: false }});
        
            case ActionTypes.CERTIFICATE_LIST_REQUEST:
                return update(this.getState(), {
                    isFetching: { $set: true }
                });

            case ActionTypes.CERTIFICATE_LIST_SUCCESS:
                return update(this.getState(), {
                    isFetching: { $set: false },
                    certificates: { $set: action.data.map(sanitise) }
                });

            case ActionTypes.CERTIFICATE_LIST_FAILURE:
                return getEmptyState();

            case ActionTypes.CERTIFICATE_INFO_REQUEST:
                return update(this.getState(), { isFetching: { $set: true }});

            case ActionTypes.CERTIFICATE_INFO_SUCCESS:
                index = this.getIndex(action.payload.id);
                if (-1 < index) {
                    certChange = { [index]: { $set: sanitise(action.data) } };
                } else {
                    certChange = { $push: [sanitise(action.data)] };
                }
                return update(this.getState(), { 
                    isFetching: { $set: false },
                    certificates: certChange
                });

            case ActionTypes.CERTIFICATE_INFO_FAILURE:
                return update(this.getState(), { isFetching: { $set: false }});

            case ActionTypes.CERTIFICATE_UPDATE_REQUEST:
                return update(this.getState(), {isFetching: {$set: true}});

            case ActionTypes.CERTIFICATE_UPDATE_SUCCESS:
                index = this.getIndex(action.payload.id);
                return update(this.getState(), {
                    isFetching: {$set: false},
                    certificates: { [index]: { $set: sanitise(action.data) } }
                });

            case ActionTypes.CERTIFICATE_UPDATE_FAILURE:
                return update(this.getState(), {isFetching: {$set: false}});


            case ActionTypes.CERTIFICATE_ADD_FIELD_REQUEST:
                index = this.getIndex(action.payload.id);
                if (-1 === index) { return state; } // guard
                loadDraft = Object.assign(action.payload.draft, {loading: true});
                if (!!this._state.certificates[index].fields) {
                    certChange = { [index]: { fields: { $push: [loadDraft] }}};
                } else {
                    certChange = { [index]: { fields: { $set: [loadDraft] }}};
                }
                return update(this.getState(), {isFetching: {$set: true}, certificates: certChange});

            case ActionTypes.CERTIFICATE_ADD_FIELD_SUCCESS:
                [index, fieldIndex] = this.getFieldIndex(action.payload.id, action.payload.draft.id);
                return update(this.getState(), {
                    isFetching: {$set: false},
                    certificates: { [index]: { fields: { [fieldIndex]: { $set: action.data } } } }
                });
            
            case ActionTypes.CERTIFICATE_ADD_FIELD_FAILURE:
                certChange = {isFetching: {$set: false}};
                [index, fieldIndex] = this.getFieldIndex(action.payload.id, action.payload.draft.id);
                if (-1 < fieldIndex) {
                    certChange.certificates = { [index]: { fields: { $splice: [[fieldIndex, 1]] } }};
                }
                return update(this.getState(), certChange);

            case ActionTypes.CERTIFICATE_UPDATE_FIELD_REQUEST:
                [index, fieldIndex] = this.getFieldIndex(action.payload.id, action.payload.current.id);
                loadDraft = Object.assign(action.payload.draft, {loading: true});
                return update(this.getState(), {
                    isFetching: {$set: false},
                    certificates: { [index]: { fields: { [fieldIndex]: { $set: loadDraft } } } }
                });

            case ActionTypes.CERTIFICATE_UPDATE_FIELD_SUCCESS:
                [index, fieldIndex] = this.getFieldIndex(action.payload.id, action.payload.current.id);
                return update(this.getState(), {
                    isFetching: {$set: false},
                    certificates: { [index]: { fields: { [fieldIndex]: { $set: action.data } } } }
                });

            case ActionTypes.CERTIFICATE_UPDATE_FIELD_FAILURE:
                [index, fieldIndex] = this.getFieldIndex(action.payload.id, action.payload.current.id);
                return update(this.getState(), {
                    isFetching: {$set: false},
                    certificates: { [index]: { fields: { [fieldIndex]: { $set: action.payload.current } } } }
                });

            case ActionTypes.CERTIFICATE_REMOVE_FIELD_REQUEST:
                return update(this.getState(), {isFetching: {$set: true}});

            case ActionTypes.CERTIFICATE_REMOVE_FIELD_SUCCESS:
                certChange = {isFetching: {$set: false}};
                [index, fieldIndex] = this.getFieldIndex(action.payload.id, action.payload.current.id);
                if (-1 < fieldIndex) {
                    certChange.certificates = { [index]: { fields: { $splice: [[fieldIndex, 1]] } }};
                }
                return update(this.getState(), certChange);

            case ActionTypes.CERTIFICATE_REMOVE_FIELD_FAILURE:
                return update(this.getState(), {isFetching: {$set: false}});

            default:
                return state;
        }
    }
}

export default new CertificateStore(CentricDispatcher);
